import React, { useState, useRef, useEffect } from "react";
import {
  View,
  StyleProp,
  TextStyle,
  TouchableWithoutFeedback,
  Dimensions,
  Platform,
  StatusBar,
  StyleSheet} from "react-native";
import { Portal } from "react-native-paper";
import { fonts } from "../styles/theme";
import AppText from "./AppText";

interface OverflowTooltipTextProps {
  testID?: string;
  text: string;
  tooltipValue?: string;
  style?: StyleProp<TextStyle>;
  containerWidth?: string | number;
  onPress?: () => void;
}

const TOOLTIP_MAX_WIDTH = Math.min(Dimensions.get("window").width * 0.85, 320);
const HIDE_DELAY = 5000;
// Distance between the bubble and the value it describes.
const GAP = 4;
// measureInWindow measures from the top of the window (status bar included),
// but the Portal draws inside the app root below it, so Android needs the
// status bar height added back or the bubble floats too high.
const STATUS_BAR_OFFSET =
  Platform.OS === "android" ? StatusBar.currentHeight ?? 0 : 0;

const OverflowTooltipText: React.FC<OverflowTooltipTextProps> = ({
  testID,
  text,
  style,
  containerWidth = "90%",
  tooltipValue,
  onPress}) => {
  const [visible, setVisible] = useState(false);
  const [anchorRect, setAnchorRect] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [bubbleHeight, setBubbleHeight] = useState(0);
  const anchorRef = useRef<View>(null);
  const hideTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  const hideTooltip = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setVisible(false);
    setAnchorRect(null);
    setBubbleHeight(0);
  };

  const showTooltip = () => {
    setVisible(true);
    anchorRef.current?.measureInWindow((x, y, width, height) => {
      setAnchorRect({ x, y, width, height });
    });
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(hideTooltip, HIDE_DELAY);
  };

  // A plain tap belongs to onPress (usually navigation); the tooltip opens on
  // a long press, the way Paper's Tooltip behaved before.
  const handlePress = () => {
    if (visible) {
      hideTooltip();
      return;
    }
    onPress?.();
  };

  // Positioned once the bubble has reported its real height, so a one-line
  // tooltip sits just as close to the value as a two-line one.
  const screen = Dimensions.get("window");
  const left = anchorRect
    ? Math.max(
        8,
        Math.min(
          anchorRect.x + anchorRect.width / 2 - TOOLTIP_MAX_WIDTH / 2,
          screen.width - TOOLTIP_MAX_WIDTH - 8,
        ),
      )
    : 0;
  const anchorTop = (anchorRect?.y ?? 0) + STATUS_BAR_OFFSET;
  const above = anchorRect ? anchorTop - bubbleHeight - GAP : 0;
  const top =
    anchorRect && above > 8
      ? above
      : anchorTop + (anchorRect?.height ?? 0) + GAP;

  return (
    <View
      ref={anchorRef}
      style={{ width: containerWidth as number }}
      testID="tooltip-container"
    >
      <TouchableWithoutFeedback
        testID={testID}
        onPress={handlePress}
        onLongPress={showTooltip}
        delayLongPress={300}
      >
        <AppText
          style={[style, { flexShrink: 1 }]}
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {text}
        </AppText>
      </TouchableWithoutFeedback>

      {visible && (
        <Portal>
          <TouchableWithoutFeedback onPress={hideTooltip}>
            <View style={StyleSheet.absoluteFill}>
              <View
                testID="tooltip-bubble"
                onLayout={(e) => setBubbleHeight(e.nativeEvent.layout.height)}
                style={[
                  styles.tooltip,
                  { top, left, opacity: anchorRect && bubbleHeight ? 1 : 0 },
                ]}
              >
                <AppText style={styles.tooltipText}>
                  {tooltipValue ?? text}
                </AppText>
              </View>
            </View>
          </TouchableWithoutFeedback>
        </Portal>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  tooltip: {
    position: "absolute",
    maxWidth: TOOLTIP_MAX_WIDTH,
    backgroundColor: "#fff",
    borderRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
    elevation: 4,
    shadowColor: "#000",
    shadowOpacity: 0.2,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
  },
  tooltipText: {
    color: "#000",
    fontSize: 14,
    lineHeight: 20,
    fontFamily: fonts.regular,
  },
});

export default OverflowTooltipText;
